import { useEffect, useRef, useState } from "react";
import { ArrowDown, ArrowUp, ChevronDown, CircleDashed, FileX2, Flame, Info, ListChecks, LoaderCircle, Minus, Scale, Zap } from "lucide-react";
import type { Dettaglio, FornituraAnalisi, OffertaAnalisi, RisultatoAnalisi, StatoDettaglio } from "@/api/analisiBolletta";
import {
  BOLLETTA_ANNO, BOLLETTA_QUESTA, BOLLETTA_SCRITTO, BOLLETTA_TITOLO, CAMBIA_CON_OFFERTA, differenza, euro, FASCE,
  mesiPeriodo, motivoBolletta, motivoOfferta, notaVendita, PREZZO_TITOLO, prezzoLetto, prezzoOfferta, quotaMese,
  TIPO_PREZZO, VENDITA_TITOLO,
} from "@/lib/analisiScritte";
import type { VoceAnalisi } from "./useAnalisiChat";
import { SezioneVoci } from "./SezioneVoci";
import { sintesiVoci } from "@/lib/vociBolletta";

// versione A del 19/10: in cima i numeri che il cliente ritrova in bolletta, sotto la parte che cambia con l'offerta
const TITOLO = BOLLETTA_TITOLO;
const LETTURA = "Lettura della bolletta in corso";

const unita = (f: FornituraAnalisi) => (f.commodity === "gas" ? "Smc" : "kWh");

function RigaOfferta({ o, f }: { o: OffertaAnalisi; f: FornituraAnalisi }) {
  const prezzo = o.prezzo ? prezzoOfferta(o.prezzo, o.tipo_prezzo, f.commodity) : null;
  const dettaglio = [prezzo, o.quota_fissa_mese ? quotaMese(o.quota_fissa_mese) : null].filter(Boolean).join(" · ");
  const confronto = o.spesa_annua !== null && o.differenza !== null && !o.motivo;
  const diff = confronto ? differenza(o.differenza as string) : null;
  const colore = diff?.verso === "meno" ? "text-brand-dark font-medium"
    : diff?.verso === "piu" ? "text-amber-800 font-medium" : "text-muted-foreground";
  const Freccia = diff?.verso === "meno" ? ArrowDown : diff?.verso === "piu" ? ArrowUp : Minus;
  if (diff?.verso === "piu") {
    // un'offerta che costa di più non dice quanto (Alfonso 19/10: «se negativo il confronto non diciamolo»)
    return (
      <li className="px-3 py-2">
        <p className="text-sm font-medium">{o.nome}</p>
        {dettaglio && <p className="text-xs text-muted-foreground tabular-nums">{dettaglio}</p>}
      </li>
    );
  }
  return (
    <li className="px-3 py-2">
      <p className="text-sm font-medium">{o.nome}</p>
      {dettaglio && <p className="text-xs text-muted-foreground tabular-nums">{dettaglio}</p>}
      {diff ? (
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-sm tabular-nums">{euro(o.spesa_annua as string)}</span>
          <span className={`flex items-center gap-1 text-xs tabular-nums ${colore}`}>
            <Freccia className="w-3 h-3 shrink-0" aria-hidden="true" />
            {diff.testo}
          </span>
        </div>
      ) : (
        <p className="text-xs mt-1 flex items-start gap-1.5">
          {o.motivo?.startsWith("fisso contro") ? (
            <Info className="w-3.5 h-3.5 mt-0.5 text-muted-foreground shrink-0" aria-hidden="true" />
          ) : (
            <CircleDashed className="w-3.5 h-3.5 mt-0.5 text-muted-foreground shrink-0" aria-hidden="true" />
          )}
          {motivoOfferta(o.motivo, {
            commodity: f.commodity, tipoBolletta: f.tipo_prezzo, tipoOfferta: o.tipo_prezzo,
            riferimentoMercato: f.riferimento_mercato,
          })}
        </p>
      )}
    </li>
  );
}

/** «Questa bolletta» (totale stampato e i suoi mesi) e «In un anno» (spesa annua stampata): una colonna per cifra
 *  letta, niente card se non ce n'è nessuna. La spesa annua è della fornitura: con luce e gas non si somma. */
function CardBolletta({ r }: { r: RisultatoAnalisi }) {
  const periodi = new Set(r.forniture.filter((f) => f.periodo_da).map((f) => mesiPeriodo(f.periodo_da as string, f.periodo_a)));
  const mesi = periodi.size === 1 ? [...periodi][0] : null;
  const annua = r.forniture.length === 1 ? r.forniture[0].spesa_annua : null;
  const colonne = [
    r.totale_bolletta ? { etichetta: BOLLETTA_QUESTA, cifra: euro(r.totale_bolletta), sotto: mesi } : null,
    annua ? { etichetta: BOLLETTA_ANNO, cifra: euro(annua.euro), sotto: BOLLETTA_SCRITTO } : null,
  ].filter((c): c is { etichetta: string; cifra: string; sotto: string | null } => c !== null);
  if (colonne.length === 0) return null;
  return (
    <div className={`bg-card rounded-lg border border-border px-3 py-2 ${colonne.length > 1 ? "grid grid-cols-2" : ""}`}>
      {colonne.map((c, i) => (
        <div key={c.etichetta} className={i > 0 ? "border-l border-border pl-3" : ""}>
          <p className="text-xs text-muted-foreground">{c.etichetta}</p>
          <p className="font-semibold text-xl tabular-nums">{c.cifra}</p>
          {c.sotto && <p className="text-xs text-muted-foreground">{c.sotto}</p>}
        </div>
      ))}
    </div>
  );
}

/** «Prezzo dell'energia»: la pastiglia dal tipo letto subito; le righe del prezzo e la quota fissa solo se lette dal
 *  controllo delle voci (luce), che arriva dopo. Una riga del variabile solo per una bolletta variabile, del fisso
 *  solo per una fissa: se non tornano col tipo letto, nessuna riga. */
function CardPrezzo({ f, dettaglio, dettaglioStato }: {
  f: FornituraAnalisi; dettaglio?: Dettaglio | null; dettaglioStato?: StatoDettaglio | null;
}) {
  const tipo = f.tipo_prezzo && TIPO_PREZZO[f.tipo_prezzo] ? f.tipo_prezzo : null;
  const p = f.commodity === "luce" ? dettaglio?.prezzo : undefined;
  const coerenti = p && ((tipo === "variabile" && p.indice) || (tipo === "fisso" && !p.indice));
  const righe = coerenti ? p.righe : [];
  // la quota fissa del conto della spesa per la vendita, non la sola commercializzazione (Alfonso 19/10: MET 14,50
  // in chat e nel conto, 12,00 nel riquadro)
  const quota = f.quota_fissa_mese ?? null;
  // il controllo delle voci sta ancora leggendo: lo spazio delle righe in attesa (ui-ux-pro-max: progressive-loading)
  const inArrivo = f.commodity === "luce" && dettaglioStato === "in_corso" && !p;
  if (!tipo && righe.length === 0 && !quota) return null;
  return (
    <div className="bg-card rounded-lg border border-border px-3 py-2 space-y-1 text-sm">
      <p className="text-xs text-muted-foreground">{PREZZO_TITOLO}</p>
      {tipo && (
        <span className="flex w-fit text-xs font-semibold rounded px-1.5 py-0.5 text-brand-dark bg-brand/10">
          {TIPO_PREZZO[tipo]}
        </span>
      )}
      {righe.map((r) => (
        <p key={r.fasce} className="tabular-nums">
          <span className="font-semibold">{prezzoLetto(r.valore, p?.unita ?? null, p?.indice ?? null)}</span>
          {FASCE[r.fasce] ? ` ${FASCE[r.fasce]}` : ""}
        </p>
      ))}
      {quota && (
        <p className="tabular-nums">Quota fissa <span className="font-semibold">{euro(quota)}</span> al mese</p>
      )}
      {inArrivo && (
        <div className="h-4 w-4/5 rounded bg-border/60 animate-pulse motion-reduce:animate-none" aria-hidden="true" />
      )}
    </div>
  );
}

function Fornitura({ f, etichetta, dettaglio, dettaglioStato }: {
  f: FornituraAnalisi; etichetta: boolean; dettaglio?: Dettaglio | null; dettaglioStato?: StatoDettaglio | null;
}) {
  const Icona = f.commodity === "gas" ? Flame : Zap;
  const intestazione = etichetta && (
    <p className="text-xs font-semibold text-muted-foreground flex items-center gap-1">
      <Icona className="w-3.5 h-3.5" aria-hidden="true" />
      {f.commodity === "gas" ? "Gas" : "Luce"}
    </p>
  );
  const prezzo = <CardPrezzo f={f} dettaglio={dettaglio} dettaglioStato={dettaglioStato} />;
  if (f.motivo || f.oggi_paghi === null) {
    return (
      <div className="space-y-3">
        {intestazione}
        {prezzo}
        <p className="text-sm flex items-start gap-2">
          <FileX2 className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" aria-hidden="true" />
          {motivoBolletta(f.motivo)}
        </p>
      </div>
    );
  }
  return (
    <div className="space-y-3">
      {intestazione}
      {prezzo}
      <p className="flex items-center gap-2 text-xs font-semibold text-muted-foreground">
        {CAMBIA_CON_OFFERTA}
        <span className="flex-1 border-t border-border" aria-hidden="true" />
      </p>
      <div className="bg-card rounded-lg border border-border px-3 py-2">
        <p className="text-xs text-muted-foreground">{VENDITA_TITOLO}</p>
        <p className="font-semibold text-xl tabular-nums">
          {euro(f.oggi_paghi)} <span className="text-sm font-normal text-muted-foreground">all'anno</span>
        </p>
      </div>
      {f.offerte.length > 0 ? (
        <ul className="bg-card rounded-lg border border-border divide-y divide-border">
          {f.offerte.map((o) => <RigaOfferta key={o.id} o={o} f={f} />)}
        </ul>
      ) : (
        <p className="text-sm flex items-start gap-2">
          <CircleDashed className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" aria-hidden="true" />
          {motivoBolletta(null)}
        </p>
      )}
      {f.altre_partite.length > 0 && (
        <div className="text-sm">
          <p className="text-xs font-semibold text-muted-foreground mb-1">Altre partite</p>
          {f.altre_partite.map((p, i) => (
            <div key={i} className="flex justify-between gap-2">
              <span>{p.descrizione}</span>
              <span className="tabular-nums">{euro(p.importo)}</span>
            </div>
          ))}
        </div>
      )}
      <p className="text-xs text-muted-foreground">{notaVendita(f.consumo_annuo, f.consumo_annuo_fonte, unita(f))}</p>
    </div>
  );
}

function Blocco({ voce }: { voce: VoceAnalisi }) {
  if (voce.stato === "in_corso") {
    return (
      <div className="space-y-3" aria-busy="true">
        <p className="text-sm text-muted-foreground flex items-center gap-2">
          <LoaderCircle className="w-4 h-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />
          {LETTURA}
        </p>
        <div className="space-y-2" aria-hidden="true">
          <div className="h-9 rounded-lg bg-border/60 animate-pulse motion-reduce:animate-none" />
          <div className="h-6 rounded bg-border/60 animate-pulse motion-reduce:animate-none w-4/5" />
          <div className="h-6 rounded bg-border/60 animate-pulse motion-reduce:animate-none w-3/5" />
        </div>
      </div>
    );
  }
  const r = voce.risultato;
  if (voce.stato !== "pronta" || !r || r.forniture.length === 0) {
    return (
      <p className="text-sm flex items-start gap-2">
        <FileX2 className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" aria-hidden="true" />
        {motivoBolletta(r?.motivo ?? null)}
      </p>
    );
  }
  return (
    <div className="space-y-3">
      <CardBolletta r={r} />
      {r.forniture.map((f, i) => (
        <div key={i} className={i > 0 ? "border-t border-border pt-3" : ""}>
          <Fornitura f={f} etichetta={r.forniture.length > 1} dettaglio={voce.dettaglio}
            dettaglioStato={voce.dettaglioStato} />
        </div>
      ))}
    </div>
  );
}

function Contenuto({ voci }: { voci: VoceAnalisi[] }) {
  return (
    <div className="space-y-4">
      {voci.map((v, i) => (
        <div key={v.id} className={`space-y-3 ${i > 0 ? "border-t border-border pt-4" : ""}`}>
          <Blocco voce={v} />
          <SezioneVoci voce={v} />
        </div>
      ))}
    </div>
  );
}

/** Riquadro fisso accanto alla chat (≥ 1024 px). Sotto `lg` non si vede: c'è la striscia. */
export function RiquadroSpesaVendita({ voci }: { voci: VoceAnalisi[] }) {
  if (voci.length === 0) return null;
  return (
    <aside
      aria-label={TITOLO}
      data-tour="spesa-vendita"
      className="hidden lg:block bg-surface/50 border-l border-border p-4 overflow-y-auto space-y-3"
    >
      <h3 className="font-heading font-semibold text-[15px] flex items-center gap-2">
        <Scale className="w-4 h-4 text-brand" aria-hidden="true" />
        {TITOLO}
      </h3>
      <Contenuto voci={voci} />
    </aside>
  );
}

/** «fino a X in meno»: sceglie tra le differenze già calcolate dal backend, non ne calcola. Col totale stampato in
 *  testa c'è la bolletta (proposta del 19/10, da approvare): il risparmio, annuo, lo dice. */
function riepilogo(voci: VoceAnalisi[]): {
  bolletta: string | null; attuale: string | null; risparmio: string | null; inCorso: boolean;
} {
  const primo = voci[0];
  const vuoto = { bolletta: null, attuale: null, risparmio: null };
  if (primo.stato === "in_corso") return { ...vuoto, inCorso: true };
  const totale = primo.stato === "pronta" && primo.risultato?.totale_bolletta ? euro(primo.risultato.totale_bolletta) : null;
  const f = primo.risultato?.forniture.find((x) => x.oggi_paghi !== null && !x.motivo);
  if (primo.stato !== "pronta" || !f) return { ...vuoto, bolletta: totale, inCorso: false };
  let migliore: string | null = null;
  for (const o of f.offerte) {
    if (!o.motivo && o.differenza !== null && Number(o.differenza) < 0
      && (migliore === null || Number(o.differenza) < Number(migliore))) migliore = o.differenza;
  }
  return { bolletta: totale, attuale: euro(f.oggi_paghi as string), risparmio: migliore ? differenza(migliore).testo : null,
    inCorso: false };
}

/** Striscia in alto sul telefono (< 1024 px): sintesi sempre in vista, il tocco apre il contenuto. `sempre`:
 *  anche oltre 1024 px (pagina QR: una colonna stretta pure al computer, senza riquadro laterale). */
export function StrisciaSpesaVendita({ voci, sempre = false, agganciata = false }: {
  voci: VoceAnalisi[]; sempre?: boolean;
  /** resta in cima allo schermo mentre la pagina scorre, solo da chiusa: aperta coprirebbe la chat */
  agganciata?: boolean;
}) {
  const [aperta, setAperta] = useState(false);
  const striscia = useRef<HTMLDivElement>(null);
  // aperta non resta agganciata e torna al suo posto in cima alla pagina, spesso fuori schermo: si porta lo schermo
  // al suo inizio, altrimenti dal telefono si vede il centro e non si capisce che sopra c'è altro (prova del 18/10;
  // ui-ux-pro-max: continuity, reduced-motion)
  useEffect(() => {
    const el = striscia.current;
    if (!aperta || !agganciata || !el || el.getBoundingClientRect().top >= 0) return;
    const ridotto = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    el.scrollIntoView({ block: "start", behavior: ridotto ? "auto" : "smooth" });
  }, [aperta, agganciata]);
  if (voci.length === 0) return null;
  const r = riepilogo(voci);
  const v2 = sintesiVoci(voci[0]);
  return (
    <div ref={striscia} className={`${sempre ? "" : "lg:hidden "}${agganciata && !aperta ? "sticky top-0 z-10 bg-card " : ""}border-b border-border`}>
      <div className="bg-surface/50">
      <button
        type="button"
        aria-expanded={aperta}
        onClick={() => setAperta((a) => !a)}
        className="w-full min-h-[48px] flex items-center justify-between gap-2 px-3 py-3 text-left"
      >
        <span className="text-sm">
          {r.inCorso ? (
            <span className="flex items-center gap-2 text-muted-foreground">
              <LoaderCircle className="w-4 h-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />
              {LETTURA}
            </span>
          ) : r.bolletta ? (
            <>
              <span className="font-medium tabular-nums">Bolletta {r.bolletta}</span>
              {r.risparmio && <> · <span className="text-brand-dark tabular-nums">fino a {r.risparmio} all'anno</span></>}
            </>
          ) : r.attuale ? (
            <>
              <span className="font-medium">Spesa attuale {r.attuale}</span>
              {r.risparmio && <> · <span className="text-brand-dark tabular-nums">fino a {r.risparmio}</span></>}
            </>
          ) : (
            <span className="font-medium">{TITOLO}</span>
          )}
          {v2 && (
            <span className="block text-xs text-muted-foreground mt-0.5">
              <span className="inline-flex items-center gap-1 tabular-nums">
                {v2.inCorso ? (
                  <LoaderCircle className="w-3.5 h-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />
                ) : (
                  <ListChecks className="w-3.5 h-3.5" aria-hidden="true" />
                )}
                {v2.testo}
              </span>
            </span>
          )}
        </span>
        <ChevronDown className={`w-4 h-4 shrink-0 transition-transform motion-reduce:transition-none ${aperta ? "rotate-180" : ""}`} aria-hidden="true" />
      </button>
      {aperta && (
        <div className="px-3 pb-3 max-h-[60vh] overflow-y-auto">
          <Contenuto voci={voci} />
        </div>
      )}
      </div>
    </div>
  );
}
